import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Worker Definitions ───────────────────────────────────────────────
const WORKERS = [
  {
    key: 'origination',
    name: 'Origination',
    description: 'Deal assessment & financial analysis',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    activityMessages: [
      'Analyzing asset class characteristics...',
      'Evaluating financial viability metrics...',
      'Assessing investment readiness score...',
      'Reviewing deal structure parameters...',
      'Computing risk-adjusted returns...',
      'Benchmarking against comparable deals...',
      'Scoring origination feasibility...',
      'Finalizing deal assessment output...',
    ],
  },
  {
    key: 'regulatory',
    name: 'Regulatory & Structuring',
    description: 'Structure selection & jurisdiction mapping',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>
    ),
    activityMessages: [
      'Mapping jurisdiction requirements...',
      'Evaluating SPV structure options...',
      'Checking regulatory licensing paths...',
      'Analyzing DFSA/VARA compliance needs...',
      'Selecting optimal legal structure...',
      'Reviewing cross-border implications...',
      'Assessing regulatory timeline...',
      'Compiling structuring recommendations...',
    ],
  },
  {
    key: 'technology',
    name: 'Technology',
    description: 'Tokenization & custody integration',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    activityMessages: [
      'Evaluating blockchain platform options...',
      'Selecting tokenization standard...',
      'Assessing custody integration paths...',
      'Reviewing smart contract requirements...',
      'Analyzing DLT infrastructure needs...',
      'Computing gas and execution costs...',
      'Mapping wallet and KYC integrations...',
      'Finalizing technology stack selection...',
    ],
  },
  {
    key: 'distribution',
    name: 'Distribution',
    description: 'Investor segmentation & placement strategy',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    activityMessages: [
      'Segmenting target investor profiles...',
      'Analyzing distribution channel reach...',
      'Evaluating placement feasibility...',
      'Mapping investor appetite by geography...',
      'Computing minimum ticket sizing...',
      'Assessing marketing channel strategy...',
      'Reviewing secondary market options...',
      'Compiling distribution recommendations...',
    ],
  },
];

const ORCHESTRATOR = {
  key: 'orchestrator',
  name: 'Orchestrator Agent',
  description: 'Assembling final proposal from all worker outputs',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  activityMessages: [
    'Merging intelligence outputs into unified structure...',
    'Generating executive summary...',
    'Structuring 14 client-facing proposal sections...',
    'Cross-referencing worker recommendations...',
    'Building decision explainability snapshot...',
    'Applying formatting and compliance checks...',
    'Optimizing proposal narrative flow...',
    'Finalizing proposal document...',
  ],
};

const SYSTEM_MESSAGES = {
  queued: [
    'Initializing proposal generation pipeline...',
    'Loading engagement context and questionnaire data...',
    'Preparing AI intelligence workers...',
    'Configuring worker parameters...',
    'Validating input data integrity...',
    'Pipeline initialization complete, dispatching workers...',
  ],
  generating_proposal: [
    'All 4 workers completed — handing off to Orchestrator Agent...',
    'Orchestrator merging intelligence outputs...',
    'Generating executive summary...',
    'Structuring proposal sections...',
    'Cross-referencing worker recommendations...',
    'Optimizing proposal narrative flow...',
    'Finalizing proposal document...',
  ],
  rating_proposal: [
    'Proposal assembled — running AI expert analysis...',
    'Analyzing proposal quality and completeness...',
    'Evaluating regulatory readiness and risk profile...',
    'Computing overall expert rating...',
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────
const formatElapsed = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
};

const getTimestamp = () => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const STATUS_LABELS = {
  queued: 'Initializing Pipeline',
  workers_running: '4 AI Workers Running in Parallel',
  generating_proposal: 'Orchestrator Agent Assembling Proposal',
  rating_proposal: 'AI Expert Analysis',
  rating_below_threshold: 'Low Rating — Awaiting Confirmation',
  completed: 'Proposal Ready',
  failed: 'Generation Failed',
};

// ─── Component ────────────────────────────────────────────────────────
const ProposalGenerationProgress = ({
  status,
  workersCompleted = 0,
  workersTotal = 4,
  workerOutputs = [],
  startTime,
  onRetry,
  rating = null,
  ratingReasons = null,
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activityLog, setActivityLog] = useState([]);
  const [workerActivityIndex, setWorkerActivityIndex] = useState(0);
  const logEndRef = useRef(null);
  const prevStatusRef = useRef(status);
  const systemMsgIndexRef = useRef(0);
  const systemMsgIntervalRef = useRef(null);

  // ── Elapsed timer ─────────────────────────────────────────────
  useEffect(() => {
    if (status === 'completed' || status === 'failed') return;
    const start = startTime || Date.now();
    const tick = () => setElapsedTime(Date.now() - start);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTime, status]);

  // ── Worker activity message rotation (every 3s) ───────────────
  useEffect(() => {
    if (status === 'completed' || status === 'failed') return;
    const id = setInterval(() => {
      setWorkerActivityIndex((prev) => prev + 1);
    }, 3000);
    return () => clearInterval(id);
  }, [status]);

  // ── Append log helper ─────────────────────────────────────────
  const appendLog = useCallback((message, type = 'system') => {
    setActivityLog((prev) => {
      const entry = { id: Date.now() + Math.random(), timestamp: getTimestamp(), message, type };
      const next = [...prev, entry];
      return next.slice(-8);
    });
  }, []);

  // ── System messages for queued / generating_proposal ──────────
  useEffect(() => {
    // Clear previous interval on status change
    if (systemMsgIntervalRef.current) {
      clearInterval(systemMsgIntervalRef.current);
      systemMsgIntervalRef.current = null;
    }

    const msgs = SYSTEM_MESSAGES[status];
    if (!msgs) return;

    // Reset index when entering a new phase
    if (status !== prevStatusRef.current) {
      systemMsgIndexRef.current = 0;
    }

    // Fire first message immediately
    if (systemMsgIndexRef.current < msgs.length) {
      appendLog(msgs[systemMsgIndexRef.current], 'system');
      systemMsgIndexRef.current += 1;
    }

    systemMsgIntervalRef.current = setInterval(() => {
      if (systemMsgIndexRef.current < msgs.length) {
        appendLog(msgs[systemMsgIndexRef.current], 'system');
        systemMsgIndexRef.current += 1;
      }
    }, 4000);

    return () => {
      if (systemMsgIntervalRef.current) {
        clearInterval(systemMsgIntervalRef.current);
        systemMsgIntervalRef.current = null;
      }
    };
  }, [status, appendLog]);

  // ── Log worker completions / status transitions ───────────────
  useEffect(() => {
    if (status !== prevStatusRef.current) {
      if (status === 'workers_running' && prevStatusRef.current === 'queued') {
        appendLog('Pipeline ready — dispatching AI workers', 'accent');
      }
      if (status === 'generating_proposal') {
        appendLog('All 4 workers completed — orchestrator assembling proposal', 'accent');
      }
      if (status === 'rating_proposal') {
        appendLog('Proposal assembled — running AI expert analysis', 'accent');
      }
      if (status === 'rating_below_threshold') {
        appendLog(`AI expert rated ${rating || '?'}/10 — below threshold`, 'error');
      }
      if (status === 'completed') {
        appendLog('Proposal generation completed successfully', 'success');
      }
      if (status === 'failed') {
        appendLog('Proposal generation failed', 'error');
      }
      prevStatusRef.current = status;
    }
  }, [status, appendLog]);

  // Log individual worker completions
  const prevCompletedRef = useRef(0);
  useEffect(() => {
    if (workersCompleted > prevCompletedRef.current) {
      const workerIdx = workersCompleted - 1;
      if (workerIdx >= 0 && workerIdx < WORKERS.length) {
        const w = WORKERS[workerIdx];
        const output = workerOutputs[workerIdx];
        const confidence = output?.confidence_score
          ? ` (${Math.round(parseFloat(output.confidence_score) * 100)}%)`
          : '';
        appendLog(`${w.name} completed${confidence}`, 'success');
      }
      prevCompletedRef.current = workersCompleted;
    }
  }, [workersCompleted, workerOutputs, appendLog]);

  // ── Auto-scroll log ───────────────────────────────────────────
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activityLog]);

  // ── Derive worker states (all 4 run in parallel) ─────────────
  const getWorkerState = (index) => {
    // If we have actual worker output data, check by index
    if (workerOutputs.length > 0 && index < workerOutputs.length) return 'completed';
    // Fallback: count-based
    if (index < workersCompleted) return 'completed';
    // All non-completed workers run in parallel when status is workers_running
    if (status === 'workers_running') return 'running';
    // After workers phase, all should show as completed
    if (status === 'generating_proposal' || status === 'rating_proposal' || status === 'rating_below_threshold' || status === 'completed') return 'completed';
    return 'pending';
  };

  // ── Progress percentage ───────────────────────────────────────
  // Queued: 5, Workers: 10-55, Orchestrator: 55-70, Rating: 70-95, Done: 100
  const getProgress = () => {
    if (status === 'completed') return 100;
    if (status === 'failed') return 0;
    if (status === 'generating_proposal') {
      // Orchestrator phase: 55-70
      const orchestratorProgress = Math.min(70, 55 + Math.floor(elapsedTime / 2000) * 3);
      return orchestratorProgress;
    }
    if (status === 'rating_proposal') {
      // Rating phase: 70-95
      const ratingProgress = Math.min(95, 70 + Math.floor(elapsedTime / 1500) * 3);
      return ratingProgress;
    }
    if (status === 'rating_below_threshold') {
      return 95; // Paused at rating complete
    }
    if (status === 'workers_running') {
      // Workers phase: 10-55
      return Math.round(10 + (workersCompleted / workersTotal) * 45);
    }
    return 5; // queued
  };

  const progress = getProgress();
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';

  // ─── FAILED STATE ─────────────────────────────────────────────
  if (isFailed) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-soft overflow-hidden">
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Generation Failed</h3>
          <p className="text-sm text-gray-500 mb-1">An error occurred during proposal generation.</p>
          <p className="text-xs text-gray-400 mb-5">Elapsed: {formatElapsed(elapsedTime)}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-700 transition-colors"
            >
              Return to Lead
            </button>
          )}
        </div>
        {/* Activity log */}
        {activityLog.length > 0 && <ActivityLog log={activityLog} logEndRef={logEndRef} />}
      </div>
    );
  }

  // ─── COMPLETED STATE ──────────────────────────────────────────
  if (isCompleted) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-soft overflow-hidden">
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Proposal Ready</h3>
          <p className="text-sm text-gray-500">Redirecting to proposal detail...</p>
        </div>
      </div>
    );
  }

  // ─── ACTIVE GENERATION STATE ──────────────────────────────────
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-2xl overflow-hidden">
      {/* Progress Bar Section */}
      <div className="px-6 py-4 bg-gray-50/90 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {STATUS_LABELS[status] || status}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">Elapsed: {formatElapsed(elapsedTime)}</span>
            <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          </div>
        </div>
        <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary via-accent to-primary-400 rounded-full transition-all duration-700 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute inset-0 overflow-hidden rounded-full">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-gray-500">
            {status === 'generating_proposal'
              ? `4/4 workers completed — Orchestrator running`
              : status === 'rating_proposal'
              ? `Proposal assembled — AI expert analysis`
              : status === 'rating_below_threshold'
              ? `Rating: ${rating || '?'}/10 — Awaiting confirmation`
              : `${workersCompleted}/${workersTotal} workers completed`}
          </span>
          <span className="text-sm font-semibold text-primary">{progress}%</span>
        </div>
      </div>

      {/* Worker Cards — 2x2 Grid */}
      <div className="px-4 sm:px-6 py-5">
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-4 rounded-lg p-2 sm:p-3 transition-all duration-300 ${
          (status === 'queued' || status === 'workers_running') ? 'ring-2 ring-primary/30 bg-primary-50/20' : ''
        }`}>
          {WORKERS.map((worker, idx) => {
            const state = getWorkerState(idx);
            const output = workerOutputs[idx];
            const msgIdx = workerActivityIndex % worker.activityMessages.length;

            return (
              <div
                key={worker.key}
                className={`rounded-lg px-3 py-3 transition-all duration-300 ${
                  state === 'running'
                    ? 'bg-primary-50/60'
                    : state === 'completed'
                    ? 'bg-green-50/40'
                    : 'bg-gray-50/40'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`flex-shrink-0 ${
                      state === 'running'
                        ? 'text-primary'
                        : state === 'completed'
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}>
                      {worker.icon}
                    </div>
                    <h4 className={`text-sm font-semibold truncate ${
                      state === 'pending' ? 'text-gray-400' : 'text-gray-900'
                    }`}>
                      {worker.name}
                    </h4>
                  </div>
                  {/* Status indicator */}
                  {state === 'completed' ? (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : state === 'running' ? (
                    <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
                      <g style={{ transformOrigin: '18px 28px', animation: 'spin 3s linear infinite' }}>
                        <circle cx="18" cy="28" r="10" stroke="#007D88" strokeWidth={1.8} opacity={0.3} />
                        <circle cx="18" cy="28" r="4" stroke="#007D88" strokeWidth={1.5} />
                        <path strokeLinecap="round" strokeWidth={2} stroke="#007D88" d="M18 16v3M18 37v3M6 28h3M27 28h3" />
                        <path strokeLinecap="round" strokeWidth={2} stroke="#007D88" d="M9.5 19.5l2.1 2.1M24.4 34.4l2.1 2.1M9.5 36.5l2.1-2.1M24.4 21.6l2.1-2.1" />
                      </g>
                      <g style={{ transformOrigin: '36px 14px', animation: 'spin 2s linear infinite reverse' }}>
                        <circle cx="36" cy="14" r="7" stroke="#25CBA1" strokeWidth={1.5} opacity={0.3} />
                        <circle cx="36" cy="14" r="3" stroke="#25CBA1" strokeWidth={1.2} />
                        <path strokeLinecap="round" strokeWidth={1.5} stroke="#25CBA1" d="M36 5v2.5M36 22v2.5M27 14h2.5M43.5 14h2.5" />
                      </g>
                    </svg>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300" />
                  )}
                </div>

                <p className={`text-xs mb-1.5 ${state === 'pending' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {worker.description}
                </p>

                {/* State-specific content */}
                {state === 'running' && (
                  <p className="text-xs text-primary animate-pulse truncate">
                    {worker.activityMessages[msgIdx]}
                  </p>
                )}
                {state === 'completed' && output && (
                  <div className="flex items-center gap-3 text-xs">
                    {output.confidence_score != null && (
                      <span className="text-green-700 font-medium">
                        {Math.round(parseFloat(output.confidence_score) * 100)}% confidence
                      </span>
                    )}
                    {output.processing_time != null && (
                      <span className="text-gray-400">
                        {output.processing_time.toFixed(1)}s
                      </span>
                    )}
                  </div>
                )}
                {state === 'completed' && !output && (
                  <span className="text-xs text-green-600 font-medium">Completed</span>
                )}
                {state === 'pending' && (
                  <span className="text-xs text-gray-400">Waiting...</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Orchestrator Agent Card — runs after workers, before rating */}
        {(() => {
          const orchState = status === 'generating_proposal'
            ? 'running'
            : (status === 'rating_proposal' || status === 'rating_below_threshold' || status === 'completed')
            ? 'completed'
            : 'pending';
          const orchMsgIdx = workerActivityIndex % ORCHESTRATOR.activityMessages.length;

          return (
            <div className="mt-4">
              <div
                className={`rounded-lg px-3 py-3 transition-all duration-300 ${
                  orchState === 'running'
                    ? 'bg-primary-50/60 ring-2 ring-primary/30'
                    : orchState === 'completed'
                    ? 'bg-green-50/40'
                    : 'bg-gray-50/40'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`flex-shrink-0 ${
                      orchState === 'running'
                        ? 'text-primary'
                        : orchState === 'completed'
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}>
                      {ORCHESTRATOR.icon}
                    </div>
                    <div className="min-w-0">
                      <h4 className={`text-sm font-semibold ${
                        orchState === 'pending' ? 'text-gray-400' : 'text-gray-900'
                      }`}>
                        {ORCHESTRATOR.name}
                      </h4>
                      <p className={`text-xs ${orchState === 'pending' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {ORCHESTRATOR.description}
                      </p>
                    </div>
                  </div>
                  {/* Status indicator */}
                  {orchState === 'completed' ? (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : orchState === 'running' ? (
                    <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
                      <g style={{ transformOrigin: '18px 28px', animation: 'spin 3s linear infinite' }}>
                        <circle cx="18" cy="28" r="10" stroke="#007D88" strokeWidth={1.8} opacity={0.3} />
                        <circle cx="18" cy="28" r="4" stroke="#007D88" strokeWidth={1.5} />
                        <path strokeLinecap="round" strokeWidth={2} stroke="#007D88" d="M18 16v3M18 37v3M6 28h3M27 28h3" />
                        <path strokeLinecap="round" strokeWidth={2} stroke="#007D88" d="M9.5 19.5l2.1 2.1M24.4 34.4l2.1 2.1M9.5 36.5l2.1-2.1M24.4 21.6l2.1-2.1" />
                      </g>
                      <g style={{ transformOrigin: '36px 14px', animation: 'spin 2s linear infinite reverse' }}>
                        <circle cx="36" cy="14" r="7" stroke="#25CBA1" strokeWidth={1.5} opacity={0.3} />
                        <circle cx="36" cy="14" r="3" stroke="#25CBA1" strokeWidth={1.2} />
                        <path strokeLinecap="round" strokeWidth={1.5} stroke="#25CBA1" d="M36 5v2.5M36 22v2.5M27 14h2.5M43.5 14h2.5" />
                      </g>
                    </svg>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300" />
                  )}
                </div>

                {/* State-specific content */}
                {orchState === 'running' && (
                  <p className="text-xs text-primary animate-pulse truncate mt-1">
                    {ORCHESTRATOR.activityMessages[orchMsgIdx]}
                  </p>
                )}
                {orchState === 'completed' && (
                  <span className="text-xs text-green-600 font-medium">Proposal assembled</span>
                )}
                {orchState === 'pending' && (
                  <span className="text-xs text-gray-400 mt-1 block">Waiting for all workers to complete...</span>
                )}
              </div>
            </div>
          );
        })()}

        {/* AI Expert Analysis Card — runs after orchestrator assembly */}
        {(() => {
          const ratingState = status === 'rating_proposal'
            ? 'running'
            : (status === 'rating_below_threshold' || status === 'completed')
            ? 'completed'
            : 'pending';

          const RATING_MESSAGES = [
            'Analyzing proposal quality and completeness...',
            'Evaluating regulatory readiness...',
            'Assessing risk profile and data quality...',
            'Computing overall expert rating...',
          ];
          const ratingMsgIdx = workerActivityIndex % RATING_MESSAGES.length;

          return (
            <div className="mt-4">
              <div
                className={`rounded-lg px-3 py-3 transition-all duration-300 ${
                  ratingState === 'running'
                    ? 'bg-amber-50/60 ring-2 ring-amber-400/30'
                    : ratingState === 'completed'
                    ? 'bg-green-50/40'
                    : 'bg-gray-50/40'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`flex-shrink-0 ${
                      ratingState === 'running'
                        ? 'text-amber-600'
                        : ratingState === 'completed'
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h4 className={`text-sm font-semibold ${
                        ratingState === 'pending' ? 'text-gray-400' : 'text-gray-900'
                      }`}>
                        AI Expert Analysis
                      </h4>
                      <p className={`text-xs ${ratingState === 'pending' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Expert evaluation of the assembled proposal quality
                      </p>
                    </div>
                  </div>
                  {ratingState === 'completed' ? (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {rating != null && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          rating >= 8 ? 'bg-green-100 text-green-700' :
                          rating >= 6 ? 'bg-blue-100 text-blue-700' :
                          rating >= 4 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {rating}/10
                        </span>
                      )}
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  ) : ratingState === 'running' ? (
                    <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
                      <g style={{ transformOrigin: '18px 28px', animation: 'spin 3s linear infinite' }}>
                        <circle cx="18" cy="28" r="10" stroke="#D97706" strokeWidth={1.8} opacity={0.3} />
                        <circle cx="18" cy="28" r="4" stroke="#D97706" strokeWidth={1.5} />
                        <path strokeLinecap="round" strokeWidth={2} stroke="#D97706" d="M18 16v3M18 37v3M6 28h3M27 28h3" />
                        <path strokeLinecap="round" strokeWidth={2} stroke="#D97706" d="M9.5 19.5l2.1 2.1M24.4 34.4l2.1 2.1M9.5 36.5l2.1-2.1M24.4 21.6l2.1-2.1" />
                      </g>
                      <g style={{ transformOrigin: '36px 14px', animation: 'spin 2s linear infinite reverse' }}>
                        <circle cx="36" cy="14" r="7" stroke="#F59E0B" strokeWidth={1.5} opacity={0.3} />
                        <circle cx="36" cy="14" r="3" stroke="#F59E0B" strokeWidth={1.2} />
                        <path strokeLinecap="round" strokeWidth={1.5} stroke="#F59E0B" d="M36 5v2.5M36 22v2.5M27 14h2.5M43.5 14h2.5" />
                      </g>
                    </svg>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300" />
                  )}
                </div>

                {ratingState === 'running' && (
                  <p className="text-xs text-amber-600 animate-pulse truncate mt-1">
                    {RATING_MESSAGES[ratingMsgIdx]}
                  </p>
                )}
                {ratingState === 'completed' && rating != null && (
                  <span className="text-xs text-green-600 font-medium">
                    Expert rated {rating}/10
                  </span>
                )}
                {ratingState === 'pending' && (
                  <span className="text-xs text-gray-400 mt-1 block">Waiting for proposal assembly...</span>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Activity Log */}
      <ActivityLog log={activityLog} logEndRef={logEndRef} />
    </div>
  );
};

// ─── Activity Log Sub-component ─────────────────────────────────────
const ActivityLog = ({ log, logEndRef }) => (
  <div className="border-t border-gray-100">
    <div className="px-6 py-2.5 flex items-center justify-between bg-gray-900">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">System Activity</span>
      <span className="inline-flex items-center gap-1.5 text-xs text-green-400 font-medium">
        <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
        Live
      </span>
    </div>
    <div className="bg-gray-900 px-6 pb-4 max-h-44 overflow-y-auto scrollbar-thin">
      {log.length === 0 ? (
        <p className="text-xs text-gray-600 font-mono py-1">Waiting for activity...</p>
      ) : (
        log.map((entry) => (
          <div key={entry.id} className="flex gap-2 py-0.5">
            <span className="text-xs text-gray-600 font-mono shrink-0">[{entry.timestamp}]</span>
            <span
              className={`text-xs font-mono ${
                entry.type === 'success'
                  ? 'text-green-400'
                  : entry.type === 'accent'
                  ? 'text-primary-400'
                  : entry.type === 'error'
                  ? 'text-red-400'
                  : 'text-gray-400'
              }`}
            >
              {entry.type === 'success' && '> '}
              {entry.message}
            </span>
          </div>
        ))
      )}
      <div ref={logEndRef} />
    </div>
  </div>
);

export default ProposalGenerationProgress;
